/**
 * PatientRecordDrawer.tsx — the PMS patient RECORD (UX Wave 6 §5): one xl,
 * tabbed, HubSpot-style drawer that interlinks everything the platform holds
 * for a patient.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 6 §5 (PMS).
 *
 * TABS
 *  - Overview — the identity block (name/email/verified/created/last login/
 *    userId) + actions: copy userId and SEND PASSWORD RESET. LANE DECISION
 *    (revised 2026-08-07): the reset now rides the ADMIN lane
 *    POST /v1/identity/admin/users/:userId/reset-email. It is keyed by USERID
 *    (not the email the public lane took), returns `{ok:true}` and NEVER a
 *    link, and audits the ADMIN as the actor. The staff route
 *    …/reset-link stays off-limits for a patient — identity 400s it, because an
 *    admin holding a patient's reset link is an account-takeover path. The
 *    PUBLIC lane it replaced (POST /v1/identity/auth/reset/request {email}) is
 *    enumeration-safe and unauthenticated, so it always 2xxs and records
 *    nobody: a staff-initiated credential change has to name its actor.
 *    ADMIN-ONLY in the UI: the clinician variant offers no reset at all.
 *    Overview also carries the TEST-RECORD UN-MARK (identity #86) — the
 *    un-mark and NOTHING ELSE: unconditional, ADMIN only. There is no "mark as
 *    a test record" affordance anywhere on the Patients screen. See
 *    `TestRecordAction` below for why a mark cannot be offered honestly from
 *    here, and for the gap that knowingly leaves.
 *  - Deals — the patient's pipeline deals. INTERIM matching (this slice): the
 *    deals list read filtered CLIENT-SIDE by the contact email (a
 *    deals-by-patient server read is the documented backend gap). Each deal
 *    deep-links "Open in Pipeline" (`?tab=pipeline`).
 *  - Subscriptions & payments — ADMIN VARIANT ONLY (money): the orders
 *    patient-context read (cases, subscriptions, ledger, totals — see
 *    api/orders.getPatientContext). Every subscription row cross-links back
 *    to the Payments drawer deep link (`?tab=orders&subscription=<id>`),
 *    completing the two-way loop with the Payments drawer's "View patient
 *    record" link. The CLINICIAN variant NEVER offers this tab and NEVER
 *    fires the read — by construction, asserted by the money-redaction sweep.
 *  - Questionnaires — read-only summaries of the patient's assessments,
 *    discovered through the deal links (assessmentId) plus — admin only — the
 *    context read's assessmentRef fields; rendered from the same money-free
 *    review read the clinical surfaces use (state/eligibility/BMI/sections —
 *    never raw JSON, no answers editing here).
 *
 * Data loading lives HERE (not per tab-panel) so switching tabs never
 * refetches: one deals read per open; one context read per open (admin only).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getAssessmentSummary } from '../api/clinic';
import { getDeals } from '../api/deals';
import { adminResetPatientPassword, markPatientTest } from '../api/identity';
import { getPatientContext } from '../api/orders';
import type { AssessmentSummary, DealCard, PatientOrdersContext, PatientPaymentKind } from '../api/types';
import { ApiError } from '../api/client';
import { formatMonthly, formatPence, formatUtcDate, formatUtcDateTime } from '../lib/format';
import { ENTRY_LABEL, STAGE_LABEL } from '../pipeline/stages';
import { CopyLinkButton, Row, SectionTitle } from '../staff/ui';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Badge, Button, Drawer, ErrorState, Help } from '../ui/kit';
import { DataTable, Td, Th } from '../ui/Table';
import { TabPanel, Tabs, type TabDef } from '../ui/Tabs';
import { DrawerSkeleton } from '../ui/skeletons';
import { labelFor, toneFor } from '../ui/tones';
import type { PmsVariant } from './PatientsTab';

/** What the drawer needs to know about the patient — a directory row, or the
 * best-effort deep-link resolution (nullable fields render as "—"). */
export interface PatientRecordSummary {
  userId: string;
  email: string | null;
  fullName: string | null;
  emailVerified: boolean | null;
  createdAt: string | null;
  lastLoginAt: string | null;
  /**
   * The ADMIN test-record flag (identity #86) — `null` when we do not know.
   *
   * Not `boolean`, because this drawer opens from TWO places: a directory row,
   * which carries the flag, and a `?patient=` deep link onto a patient who is
   * not on any loaded page, where all we resolve is a name and an email. A
   * `false` default there would be a claim the deep-link path cannot support,
   * and it would render an "un-flagged" story over a patient who may be
   * flagged. `null` renders no flag story at all.
   */
  isTest: boolean | null;
  /**
   * ⚠️ "WE HAVE NOT READ IT YET" — THE STATE `isTest: null` CANNOT EXPRESS,
   * AND THE WHOLE REASON THIS FIELD EXISTS (2026-09-05, defect review).
   *
   * `isTest` is `null` from the FIRST RENDER of every route, because the
   * directory read that carries the flag is still in flight and `selectedRow`
   * is null until it lands. Reading `null` on its own as "we could not read
   * the flag" therefore made this drawer assert a FAILED read during a
   * perfectly healthy one: deep-link an ORDINARY, UNFLAGGED patient who is
   * sitting on page 1 and the Test record heading and "we could not read
   * whether it is marked as a test record" appeared for the duration of the
   * directory read, then vanished when the row arrived. That is a wrong claim
   * on the MAJORITY path — and it is the honest-statement lane's own argument
   * turned against it: do not say what the read does not support.
   *
   * TRUE while any read that could still answer the question is outstanding:
   * the directory page read, and — on the deep-link route — the contact
   * lookup that stands in for it. FALSE once the answer is in, or once
   * nothing further is coming. `PatientsTab` owns the computation because it
   * is the component holding both statuses; this drawer only renders it.
   */
  testFlagPending: boolean;
}

/** Ledger `kind` display labels (contracts PaymentKind). */
const PAYMENT_KIND_LABEL: Record<PatientPaymentKind, string> = {
  month1_preauth: 'Month-1 hold',
  callback_49: 'Callback fee',
  renewal: 'Renewal',
};

const moneyCellStyle = {
  textAlign: 'right',
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
} as const;

export function PatientRecordDrawer({
  patient,
  variant,
  onClose,
  onTestFlagChange,
}: {
  patient: PatientRecordSummary;
  variant: PmsVariant;
  onClose: () => void;
  /**
   * The test flag was written (identity #86) — the directory patches the row it
   * already holds so the chip and the strikethrough behind this drawer agree
   * with what just happened. Optional: the drawer is mounted from one screen
   * today, and a caller that cannot patch a cached row should not be forced to
   * pretend it can.
   */
  onTestFlagChange?: (userId: string, isTest: boolean) => void;
}) {
  const [activeTab, setActiveTab] = useState('overview');

  // One deals read per open — the Deals AND Questionnaires tabs share it.
  const [deals, setDeals] = useState<DealCard[] | null>(null);
  const [dealsStatus, setDealsStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const loadDeals = useCallback(() => {
    setDealsStatus('loading');
    getDeals()
      .then((data) => {
        setDeals(data.items);
        setDealsStatus('ready');
      })
      .catch(() => setDealsStatus('error'));
  }, []);
  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  // One context read per open — ADMIN ONLY (money). The clinician variant
  // must never even request it (money-redaction sweep asserts this).
  const [context, setContext] = useState<PatientOrdersContext | null>(null);
  const [contextStatus, setContextStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const loadContext = useCallback(() => {
    if (variant !== 'admin') return;
    setContextStatus('loading');
    getPatientContext(patient.userId)
      .then((data) => {
        setContext(data);
        setContextStatus('ready');
      })
      .catch(() => setContextStatus('error'));
  }, [variant, patient.userId]);
  useEffect(() => {
    loadContext();
  }, [loadContext]);

  // INTERIM (this slice): match the patient's deals by contact email — the
  // documented gap is a clinic deals-by-patient read; until it lands, the
  // list read is filtered client-side.
  const myDeals = useMemo(() => {
    if (!deals || !patient.email) return [];
    const email = patient.email.toLowerCase();
    return deals.filter((d) => d.contact.email.toLowerCase() === email);
  }, [deals, patient.email]);

  // Assessment ids: deal links first, then (admin) the context's case refs.
  const assessmentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const deal of myDeals) if (deal.assessmentId) ids.add(deal.assessmentId);
    for (const order of context?.orders ?? []) if (order.assessmentRef) ids.add(order.assessmentRef);
    return Array.from(ids);
  }, [myDeals, context]);

  const tabs: TabDef[] = useMemo(() => {
    const base: TabDef[] = [
      { id: 'overview', label: 'Overview' },
      { id: 'deals', label: 'Deals' },
    ];
    // The money tab exists ONLY on the admin variant — a clinician drawer has
    // no Subscriptions & payments tab by construction, not by styling.
    if (variant === 'admin') base.push({ id: 'money', label: 'Subscriptions & payments' });
    base.push({ id: 'questionnaires', label: 'Questionnaires' });
    return base;
  }, [variant]);

  const title = patient.fullName?.trim() || patient.email || patient.userId;

  return (
    <Drawer open onClose={onClose} title={`Patient — ${title}`} width="xl">
      <Tabs label="Patient record sections" tabs={tabs} activeId={activeTab} onChange={setActiveTab} idBase="patient-record" />

      <TabPanel idBase="patient-record" tabId="overview" activeId={activeTab}>
        <OverviewPanel patient={patient} variant={variant} onTestFlagChange={onTestFlagChange} />
      </TabPanel>

      <TabPanel idBase="patient-record" tabId="deals" activeId={activeTab}>
        <DealsPanel patient={patient} deals={myDeals} status={dealsStatus} onRetry={loadDeals} variant={variant} />
      </TabPanel>

      {variant === 'admin' ? (
        <TabPanel idBase="patient-record" tabId="money" activeId={activeTab}>
          <MoneyPanel context={context} status={contextStatus} onRetry={loadContext} />
        </TabPanel>
      ) : null}

      <TabPanel idBase="patient-record" tabId="questionnaires" activeId={activeTab}>
        <QuestionnairesPanel assessmentIds={assessmentIds} dealsStatus={dealsStatus} />
      </TabPanel>
    </Drawer>
  );
}

/* ── Overview — the identity block + actions ─────────────────────────────── */

function OverviewPanel({
  patient,
  variant,
  onTestFlagChange,
}: {
  patient: PatientRecordSummary;
  variant: PmsVariant;
  onTestFlagChange?: (userId: string, isTest: boolean) => void;
}) {
  const [resetState, setResetState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  // Resetting someone else's credentials pauses on the shared confirmation
  // first (the G2 grammar every staff-initiated action uses).
  const [confirming, setConfirming] = useState(false);

  // ADMIN-only: a clinician has no business changing a patient's credentials,
  // and the route 403s them anyway — mirror the server rather than offer a
  // button that can only fail ("the UI is never the control", R41).
  const canReset = variant === 'admin';

  function sendReset() {
    setResetState('sending');
    // The ADMIN lane, keyed by userId — see the module doc block. It returns
    // `{ok:true}`; there is no link in the response to surface, ever.
    adminResetPatientPassword(patient.userId)
      .then(() => setResetState('sent'))
      .catch(() => setResetState('error'));
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <div>
        <SectionTitle>Identity</SectionTitle>
        <Row label="Name" value={patient.fullName?.trim() || '—'} />
        <Row label="Email" value={patient.email ?? '—'} />
        <Row
          label="Email verified"
          value={
            patient.emailVerified === null ? (
              '—'
            ) : (
              <Badge tone={patient.emailVerified ? 'positive' : 'pending'}>
                {patient.emailVerified ? 'Verified' : 'Unverified'}
              </Badge>
            )
          }
        />
        <Row label="Created" value={formatUtcDate(patient.createdAt) || '—'} />
        <Row label="Last login" value={formatUtcDateTime(patient.lastLoginAt) || '—'} />
        <Row
          label="User ID"
          value={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--gps-s-2)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <code style={{ fontSize: '12.5px' }}>{patient.userId}</code>
              <CopyLinkButton value={patient.userId} label="Copy user ID" variant="link" />
            </span>
          }
        />
      </div>

      {canReset ? (
        <div>
          <SectionTitle>Actions</SectionTitle>
          {resetState === 'sent' ? (
            <Alert tone="success" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
              A reset link has been emailed to the patient.
            </Alert>
          ) : null}
          {resetState === 'error' ? (
            <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
              Sorry — the reset email could not be sent. Please try again.
            </Alert>
          ) : null}
          {/* Keyed by userId, so this works even for a record with no email on
              file — identity owns the address it sends to, and the drawer's
              copy may be stale. */}
          <Button
            type="button"
            variant="ghost"
            onClick={() => setConfirming(true)}
            disabled={resetState === 'sending'}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          >
            {resetState === 'sending' ? 'Sending…' : 'Send password reset email'}
          </Button>
          <Help style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>
            The patient receives the reset link by email — for security, reset links for patient accounts are
            never shown here.
          </Help>

          <ConfirmDialog
            open={confirming}
            title="Send a password reset email?"
            body={
              patient.email
                ? `We will email a reset link to ${patient.email}. Their current password keeps working until they use it.`
                : 'We will email a reset link to this patient. Their current password keeps working until they use it.'
            }
            confirmLabel="Send reset email"
            onConfirm={() => {
              setConfirming(false);
              sendReset();
            }}
            onCancel={() => setConfirming(false)}
          />
        </div>
      ) : null}

      {/* ⚠️ ADMIN ONLY, and the SAME gate as the reset above — identity's
          `@Roles('ADMIN')` on the mark route is the control (R41); this is the
          affordance. A CLINICIAN gets nothing here rather than a control that
          could only ever 403. They still SEE the flag in the directory (the
          muted chip, the struck-through name): showing that a record IS a test
          record is a different act from offering to change it. */}
      {canReset ? <TestRecordAction patient={patient} onTestFlagChange={onTestFlagChange} /> : null}
    </div>
  );
}

/* ── The test-record flag — the SCOPED route back to identity #86 ────────── */

/**
 * TestRecordAction — the ADMIN-only patient test-record UN-MARK (identity #86),
 * plus the one thing it says when it cannot know whether to offer that un-mark.
 *
 * ⚠️ IT IS THE UN-MARK AND NOTHING ELSE. There is no "Mark as a test record"
 * on the Patients screen, in any state, for any role. If you are here to add
 * one back, read this whole block first: one was added on 2026-09-05, reviewed
 * the same day, and removed again because the gate it stood on cannot be built
 * out of the data this screen has.
 *
 * WHY THE MARK CAME OUT (the gate was unreliable BY CONSTRUCTION, not by bug).
 * The mark was offered only to "a patient with no deals" — the cohort the
 * clinic-service cascade cannot reach. That cohort was derived by asking
 * whether this drawer's deals array was empty, and an empty array does not
 * mean it:
 *   - `getDeals()` requests `/v1/clinic/deals?perLane=25`. There is no
 *     per-patient key on that read — none exists.
 *   - clinic-service answers it PER LANE with `take: 25, skip: 0, orderBy
 *     createdAt desc`, so what comes back is the most recent page of each lane.
 *   - the drawer then filters THAT PAGE client-side by contact email.
 * So "no deals matched" also means "their deal is older than the 25th in its
 * lane". That is not an edge case: `BOARD_PER_LANE` was raised 20 → 25 on
 * 2026-09-04 precisely BECAUSE a live lane was overflowing, and it gets worse
 * as lanes fill. An established patient with a real deal would therefore have
 * been offered the mark under the sentence "this account has no deals" — and
 * marking them writes `User.isTest` while their deal and their order case stay
 * unmarked and counted. That is exactly the HALF-MARKED record this whole
 * change exists to prevent, manufactured by the gate meant to prevent it. And
 * because the Deals tab reads the SAME array, the operator gets corroborating
 * false evidence one tab away: internally consistent, and wrong.
 *
 * Fail-closed on `loading`/`error` did not save it either. Those cover "we
 * could not ask"; this defect is a read that SUCCEEDS and answers a question
 * nobody asked it.
 *
 * WHY THE UN-MARK STAYS, UNCONDITIONALLY — AND ON DIFFERENT GROUNDS THAN IT
 * WAS ADDED ON. It arrived on 2026-09-05 argued from "the cascade is not
 * merged to `dev`, so nothing else can clear this flag". ⚠️ THAT REASON HAS
 * EXPIRED: the cascade landed the same week (clinic `dev` = 9fb728c, orders
 * `dev` = 46784b3, identity `dev` = fdc9d21) and the deal route clears all
 * three flags today. The control is still right, for three reasons that are
 * properties of the cascade's DESIGN rather than of its release date — all
 * three read off clinic `dev`'s `DealsService.resolvePatientLeg`:
 *  1. THE CASCADE DELIBERATELY WITHHOLDS THE HUMAN LEG, AND THAT IS A
 *     CLINICAL-SAFETY RULE, NOT A GAP. `User.isTest` is a claim about a HUMAN
 *     while this action is a decision about ONE JOURNEY, so the cascade moves
 *     the human flag only when every OTHER deal of that patient in the tenant
 *     already agrees with where it is going. One patient, two journeys, one
 *     account: mark journey B as a rehearsal while journey A is live and the
 *     human would vanish from the directory the clinician is searching. The
 *     rule MIRRORS on the way back — it refuses to CLEAR the human flag while
 *     their other journeys are still test records — so those patients cannot
 *     be un-flagged by the deal route in either direction. They can only be
 *     un-flagged here.
 *  2. A PATIENT WITH NO DEAL IS UNREACHABLE BY THE DEAL ROUTE ENTIRELY. There
 *     is no card to start the action from, and the patient leg resolves
 *     nothing for a deal with no assessment or no `patientUserId` anyway.
 *  3. AN OPERATOR MUST ALWAYS BE ABLE TO UNDO A MISTAKE, and the mistake this
 *     undoes hides a HUMAN from the list clinicians search to FIND patients.
 *  It also needs NO deal information at all, so it does not touch the
 *  unreliable read below. No deal test, no cascade test, no "is this
 *  reachable" test. If a future change adds a condition to this button, it has
 *  re-created the bug.
 *
 * WHY NO MARK AT ALL RATHER THAN A BEST-EFFORT ONE. The routine path for
 * marking is the DEAL — clinic-service's cascade marks the deal, its plan and
 * its patient in one action, which is what stops the three independent marks
 * that used to write three different halves of one record. A mark here cannot
 * be offered honestly without a per-patient deal read, and there is no such
 * read; guessing from a bounded board page is worse than not offering it.
 * Removing it restores the original instruction cleanly: the deal is where a
 * test record gets marked.
 *
 * ⚠️ THE GAP THIS KNOWINGLY LEAVES — DO NOT CLOSE IT FROM THE PORTAL.
 * A directly-registered QA account with NO deal (the shape of
 * `test400@mailinator.com` in `testing/fixtures.ts`) can no longer be flagged
 * from the UI at all. It has no deal drawer to start from, so the deal route
 * does not reach it either, cascade or no cascade. Marking one is an
 * out-of-band job until the SERVER CHANGE that closes it lands:
 *
 *     a deals-by-patient read on clinic-service — e.g.
 *     `GET /v1/clinic/deals?patientUserId=<id>` (or `contactEmail=`),
 *     unbounded by lane paging, returning that patient's deals and only theirs.
 *
 * With that read, a mark here could state the cohort as a fact instead of
 * inferring it from a board page, and this component could grow the mark back.
 * WITHOUT it, any mark on this screen is the same defect wearing new copy.
 *
 * ⚠️ THE CASCADE IS ON `dev` (verified 2026-09-05: clinic `dev` = 9fb728c,
 * whose `OrdersPort.markTest(orderCaseId, isTest, reason?)` takes an explicit
 * boolean and therefore UN-marks as well as marks; orders `dev` = 46784b3,
 * where the un-mark lane is reachable and audited as `order_unmarked_test`;
 * identity `dev` = fdc9d21). This block said "not merged to dev" for a day
 * and a half, which was true when written and false afterwards — the SAME
 * tense failure the docs warn about ("do not write 'now cascades' until it
 * lands"), just inverted. A stale claim is stale in whichever direction it
 * points, so re-ground it against the repos rather than trusting the sentence
 * you find here.
 *
 * WHAT LANDING CHANGED, AND WHAT IT DID NOT: the deal route now writes AND
 * clears a patient's flag, so "nothing else can clear it" is gone as an
 * argument. The three grounds listed above are what hold the un-mark up now,
 * and the first of them is the one to re-read before deleting anything — the
 * cascade withholds the human leg by design, so the deal route is not a
 * complete un-mark and never will be.
 *
 * ⚠️ THE SECOND SERVER GAP — THE UNREADABLE FLAG ON THE DEEP-LINK ROUTE.
 * Separate from the deals gap above, and the reason this component renders an
 * honest statement instead of a control on one route. Opening
 * `?tab=patients&patient=<id>` for a record that is not on a loaded directory
 * page leaves us with the batched CONTACT lookup
 * (`POST /v1/identity/admin/users/lookup`), which answers
 * `{userId, email, fullName}` — `fullName` since 2026-09-06; this used to read
 * "and — one day — `fullName`". It still carries no `isTest`, and there is no
 * single-patient patient-directory read in this portal's API layer at all:
 * `listPatients()` is the only source of the flag and it is a PAGED LIST whose
 * default excludes flagged rows. So on that route the flag is unknown, full
 * stop. The SERVER CHANGE that would close it properly (checked with the CTO
 * 2026-09-05, and DELIBERATELY NOT BUILT HERE — it is an identity-service
 * change, not a portal one):
 *
 *     a single-patient identity read that CARRIES `isTest` — e.g.
 *     `GET /v1/identity/admin/patients/<id>` returning the same
 *     `AdminPatientRow` shape the list returns; or the list read taught to
 *     accept one id together with `includeTest`, so a deep link can ask for
 *     exactly that patient without paging the directory.
 *
 * With either, `PatientsTab`'s fallback could set a REAL `isTest`, this
 * component would render the un-mark on the deep-link route like any other,
 * and the statement below would delete itself. Until then: state the gap.
 *
 * WHY THE STATEMENT IS ADMIN-ONLY (decision, 2026-09-05). It sits INSIDE this
 * component, which `OverviewPanel` renders only behind `canReset` — so a
 * CLINICIAN sees nothing, exactly as they see no un-mark. Deliberate:
 *  - a clinician cannot act on it in either direction. The mark route is
 *    identity's `@Roles('ADMIN')`, so the "turn Show test records on and open
 *    the row" instruction leads them to a drawer with no control in it. Telling
 *    someone about a gap in an action they are not permitted to take is noise
 *    dressed as transparency, and this drawer is a clinical surface.
 *  - the clinician never even REACHES this state by the same path: the
 *    contact-lookup fallback in `PatientsTab` is itself ADMIN-only (the
 *    clinician variant renders the unresolved record rather than fire a
 *    request the gateway 403s), so for them the deep-link drawer is already
 *    visibly incomplete — a raw id and dashes — rather than the convincing
 *    near-complete record that made this defect worth fixing.
 *  - a clinician CAN still see that a record is a test record, on the route
 *    that has the data: the muted chip and the struck-through name in the
 *    directory's audit view. Showing is not acting, and that split is
 *    unchanged.
 * If a clinician-facing need ever appears, the honest place for it is the
 * absence itself, not a second copy of this section.
 *
 * The dialog copy is worded from scratch rather than borrowed from the
 * pipeline's: the thing being un-hidden is a person with an account, a consent
 * history and possibly live treatment.
 */
function TestRecordAction({
  patient,
  onTestFlagChange,
}: {
  patient: PatientRecordSummary;
  onTestFlagChange?: (userId: string, isTest: boolean) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The last completed write, for the live region. Nothing else on this panel
   * moves when the flag changes — the row that changes is in the table BEHIND
   * the drawer — so without this a screen-reader user presses a control and
   * hears nothing at all (WCAG 4.1.3).
   */
  const [notice, setNotice] = useState<string | null>(null);

  const who = patient.fullName?.trim() || patient.email || 'this patient';

  async function run(reason?: string) {
    setBusy(true);
    setError(null);
    try {
      // Always the UNDO direction — `revert:true` is the only body this
      // component ever sends, because the mark direction is not offered here.
      const result = await markPatientTest(patient.userId, {
        ...(reason ? { reason } : {}),
        revert: true,
      });
      onTestFlagChange?.(result.userId, result.isTest);
      setNotice(`${who} counts as a real record again.`);
      setConfirming(false);
    } catch (err) {
      /**
       * VERBATIM where identity gave us words. Its refusals here are ids plus a
       * state sentence — PHI-free by construction (R19) — and they are the only
       * ones that say what to do instead: a non-PATIENT target is 400 because
       * the column is PATIENT-only, and an unknown or foreign-tenant id is 404.
       * A house "please try again" over either leaves an admin re-pressing a
       * control that can never work.
       */
      setError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Sorry — we could not unmark this record. Please try again.',
      );
    }
    setBusy(false);
  }

  const flagged = patient.isTest === true;
  /**
   * THE THIRD STATE, AND IT IS NOT "NOT FLAGGED" — NOR IS IT "NOT READ YET".
   *
   * `isTest === null` means no read has ANSWERED, and that covers two
   * situations that look identical in the value and could not be less alike
   * in what the screen owes the operator:
   *  - A READ IS STILL OUTSTANDING (`testFlagPending`). The everyday first
   *    render, including the majority route where the row is about to arrive
   *    on page 1. Nothing has gone wrong and nothing is known yet, so the
   *    honest thing to render is NOTHING.
   *  - EVERY READ THAT COULD ANSWER HAS SETTLED AND NONE CARRIED THE FLAG.
   *    The `?patient=<id>` deep link into a record that is on no loaded
   *    directory page, where the contact-lookup fallback resolves a name and
   *    an email and nothing else. THAT is the unreadable flag, and it is the
   *    only state the statement below is entitled to make its claim in.
   *
   * Conflating them (the 2026-09-05 defect) made this drawer state a failed
   * read while the read was in flight — see `testFlagPending` on
   * `PatientRecordSummary` for the repro.
   */
  const flagUnknown = patient.isTest === null && !patient.testFlagPending;

  /**
   * Rendered over a patient we KNOW is flagged — one we have JUST un-flagged
   * from here — or one whose flag we could not read at all.
   *  - `true`  — the un-mark, unconditionally (the whole point of this file).
   *  - `false` — nothing to offer: the mark is gone, so an un-flagged patient
   *    has no test-record action of any kind on this screen.
   *  - `null` AND SETTLED — no control, but a STATEMENT. We cannot offer the
   *    un-mark (its label asserts the patient IS flagged, which this read
   *    cannot support) and we must not stay silent either — silence reads as
   *    "not a test record", and the route it is reached from is the Payments
   *    drawer's "View patient record" cross-link, i.e. the one an operator
   *    undoing a mis-mark is most likely to arrive on.
   *
   * ⚠️ `notice` KEEPS THE SECTION ALIVE AFTER THE FLAG CLEARS, AND THAT IS
   * LOAD-BEARING, NOT COSMETIC. A successful un-mark flips `patient.isTest` to `false`
   * through `onTestFlagChange`, so without this clause the whole section —
   * including the live region that just received the confirmation — unmounts
   * in the same commit. An operator would press "Unmark" and watch the panel
   * silently empty; a screen-reader user would hear nothing at all, which is
   * exactly the WCAG 4.1.3 failure `notice` exists to prevent.
   */
  if (!flagged && !flagUnknown && !notice) return null;

  return (
    <div>
      <SectionTitle>Test record</SectionTitle>

      {notice ? (
        <Alert tone="success" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          {notice}
        </Alert>
      ) : null}

      {flagged ? (
        <>
          <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
            This account is marked as a test record, so it is hidden from the Patients directory.
          </Alert>
          {/* ⚠️ NO CONDITION ON THIS BUTTON, AND NOTHING IT WAITS FOR. It does
              not read the deals load, so a slow or failed deals read cannot
              take the undo away from an operator — see the block above. */}
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setError(null);
              setConfirming(true);
            }}
            disabled={busy}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          >
            {busy ? 'Unmarking…' : 'Unmark as a test record'}
          </Button>
          <Help style={{ marginTop: 'var(--gps-s-2)', marginBottom: 0 }}>
            Clearing the flag puts the account back in the directory. It is always available, whatever
            state their deals are in — an operator has to be able to undo a mistake.
          </Help>
        </>
      ) : null}

      {/* ⚠️ THE HONEST STATEMENT — the screen says what it does not know.
          Added 2026-09-05 after a review reproduced this: open
          `/admin?tab=patients&patient=<id>` for a FLAGGED patient and the
          drawer looks COMPLETE — real name, real email — while silently
          omitting the only control that can un-hide that person. The default
          directory read excludes flagged rows, so the row is on no loaded
          page, the contact-lookup fallback fires, and the flag arrives `null`.
          Because `flagged` is `isTest === true`, `null` hid this section
          entirely. And that URL is precisely the Payments drawer's "View
          patient record" target, so the product handed operators the one route
          that could not undo.

          ⚠️ AND IT ONLY SPEAKS ONCE THE READS HAVE SETTLED (`flagUnknown`
          takes `testFlagPending` into account). The first cut keyed off
          `isTest === null` alone, which is ALSO the value during a healthy
          load, so this alert fired over ordinary unflagged patients for the
          length of the directory read. A screen that cries "we could not read
          it" while it is reading it is not more honest than the silent
          version — it is the same failure pointing the other way.

          The undo is still REACHABLE (Patients → Filters → Show test records →
          click the row), so this is not a one-way door — it is a screen that
          lies by omission. The words below name that working route, because a
          statement that only says "unknown" leaves the operator nowhere.

          WHY A STATEMENT AND NOT A RESOLVED FLAG. There is no single-patient
          read in the portal's API layer that carries `isTest` (see the SERVER
          CHANGE note in the block above this component). Guessing the flag is
          the same class of defect as the deleted mark's deal gate: an answer
          inferred from a read that was never asked the question. So we state
          the gap instead of papering over it.

          ⚠️ DO NOT render an "Unmark" here. Its label asserts the patient IS
          flagged; over an unknown flag that is a claim we cannot support, and
          pressing it would POST `revert:true` over a patient who may never
          have been marked. */}
      {flagUnknown ? (
        <>
          <Alert tone="info" style={{ marginBottom: 'var(--gps-s-3)' }}>
            This record was opened by a direct link, so we could not read whether it is marked as a
            test record.
          </Alert>
          <Help style={{ marginTop: 0, marginBottom: 0 }}>
            To see or change it, open this patient from the Patients list with “Show test records”
            turned on.
          </Help>
        </>
      ) : null}

      <ConfirmDialog
        open={confirming}
        title={`Unmark ${who} as a test record`}
        body={
          <>
            <p style={{ margin: '0 0 8px' }}>
              This clears the test flag, so this account appears in the Patients directory again and
              counts as a real record.
            </p>
            {error ? (
              <span role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
                {error}
              </span>
            ) : null}
          </>
        }
        confirmLabel={busy ? 'Unmarking…' : 'Unmark test record'}
        busy={busy}
        optionalReason
        reasonLabel="Why is this a real record? (optional)"
        onConfirm={(reason) => void run(reason)}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
/* ── Deals — the patient's pipeline history ──────────────────────────────── */

function DealsPanel({
  patient,
  deals,
  status,
  onRetry,
  variant,
}: {
  patient: PatientRecordSummary;
  deals: DealCard[];
  status: 'loading' | 'ready' | 'error';
  onRetry: () => void;
  variant: PmsVariant;
}) {
  const [searchParams] = useSearchParams();

  if (status === 'loading') return <DrawerSkeleton blocks={2} label="Loading the patient's deals…" />;
  if (status === 'error') return <ErrorState message="Sorry — we could not load the deals." onRetry={onRetry} />;
  if (!patient.email) {
    return <Help style={{ marginBottom: 0 }}>No email on file — deals cannot be matched to this record.</Help>;
  }
  if (deals.length === 0) return <Help style={{ marginBottom: 0 }}>No deals for this patient yet.</Help>;

  // "Open in Pipeline" keeps the console context (`?clinic=` etc.), swaps the
  // section and drops the record param — landing on the board this deal lives on.
  const pipelineSearch = new URLSearchParams(searchParams);
  pipelineSearch.set('tab', 'pipeline');
  pipelineSearch.delete('patient');
  const pipelineHref = `${variant === 'admin' ? '/admin' : '/clinicians'}?${pipelineSearch.toString()}`;

  return (
    <div>
      <SectionTitle>Deals</SectionTitle>
      <ul aria-label="Patient deals" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--gps-s-3)' }}>
        {deals.map((deal) => (
          <li
            key={deal.dealId}
            style={{
              border: '1px solid var(--gps-border)',
              borderRadius: 'var(--gps-r-sm)',
              padding: 'var(--gps-s-3)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--gps-s-3)',
            }}
          >
            <Badge tone={toneFor(deal.stage)}>{STAGE_LABEL[deal.stage]}</Badge>
            <span style={{ fontSize: '13.5px', color: 'var(--gps-muted)' }}>{ENTRY_LABEL[deal.entryPath]}</span>
            <span style={{ fontSize: '13.5px', color: 'var(--gps-muted)' }}>Created {formatUtcDate(deal.createdAt)}</span>
            <Link to={pipelineHref} style={{ marginLeft: 'auto', fontSize: '13.5px', fontWeight: 600, color: 'var(--gps-link)' }}>
              Open in Pipeline
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Subscriptions & payments — the ADMIN money view ─────────────────────── */

/**
 * The one origin a Stripe deep link may point at.
 *
 * ⚠️ DEFENCE IN DEPTH, NOT DISTRUST OF OUR OWN SERVER. orders-service allow-lists
 * the `cus_…` and builds the whole URL, so this check should never fire. It is
 * here because of WHERE the value lands: an `href` inside the console of the most
 * privileged user we have. If a future revision of that read ever let something
 * else through — a `javascript:` URL, a look-alike host — the cost is an admin one
 * click away from an attacker-chosen page while signed in to everything. A link we
 * cannot vouch for is not a link, and the check is one `URL` parse.
 */
const STRIPE_DASHBOARD_ORIGIN = 'https://dashboard.stripe.com';

/** The href to render, or null when there is nothing we will stand behind. */
function safeStripeHref(url: string | null): string | null {
  if (!url) return null;
  try {
    // Absolute-only by construction: `new URL` throws on a relative path, and the
    // origin comparison pins BOTH the scheme and the host, so neither
    // `http://dashboard.stripe.com` nor `https://dashboard.stripe.com.evil.example`
    // survives it.
    return new URL(url).origin === STRIPE_DASHBOARD_ORIGIN ? url : null;
  } catch {
    return null;
  }
}

/**
 * The admin's way out of this record and into the payment processor, for a
 * patient who has added a card (orders #214).
 *
 * ⚠️ RENDERS NOTHING WHEN THERE IS NO CUSTOMER, AND THAT IS THE COMMON PATH — an
 * assessment reaches clinical review BEFORE the card form, so most patients have
 * no Stripe customer at all. No heading, no placeholder, no disabled control: a
 * greyed button or an em-dash beside a "Stripe" label both read as "we lost it",
 * when the truth is there is nothing to point at.
 *
 * The URL is rendered VERBATIM. Which dashboard a `cus_…` belongs to (live, or the
 * `/test/` variant) is decided server-side from deployment configuration; a copy
 * of that rule here would drift, and a wrong-mode link does not fail visibly —
 * Stripe signs the admin in and shows "no such customer" for a patient who is
 * sitting in front of them.
 *
 * A real anchor with an href, so it is in the tab order like every other control
 * in this drawer; `noopener noreferrer` because a third-party destination gets no
 * handle on the portal window and no referrer naming the screen it came from.
 */
function StripeCustomerLink({ url }: { url: string | null }) {
  const href = safeStripeHref(url);
  if (!href) return null;

  return (
    <div>
      <SectionTitle>Payment processor</SectionTitle>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--gps-link)', fontWeight: 600 }}
      >
        Open this customer in Stripe ↗
      </a>
      <Help style={{ marginBottom: 0 }}>
        Opens the customer&rsquo;s Stripe profile in a new tab — cards, charges, refunds and disputes
        across every order. You will need to be signed in to Stripe.
      </Help>
    </div>
  );
}

function MoneyPanel({
  context,
  status,
  onRetry,
}: {
  context: PatientOrdersContext | null;
  status: 'loading' | 'ready' | 'error';
  onRetry: () => void;
}) {
  const [searchParams] = useSearchParams();

  if (status === 'loading') return <DrawerSkeleton blocks={4} label="Loading subscriptions and payments…" />;
  if (status === 'error' || !context) {
    return <ErrorState message="Sorry — we could not load the subscriptions and payments." onRetry={onRetry} />;
  }

  // Each subscription row links back into the Payments drawer — the reverse
  // of the Payments drawer's "View patient record" link (two-way deep links).
  function paymentsHref(subscriptionId: string): string {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'orders');
    next.set('subscription', subscriptionId);
    next.delete('patient');
    return `/admin?${next.toString()}`;
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <div>
        <SectionTitle>Totals</SectionTitle>
        <Row label="Order cases" value={context.totals.orderCount} />
        <Row label="Transactions" value={context.totals.transactionCount} />
        <Row label="Total charged" value={formatPence(context.totals.chargedPence)} />
      </div>

      {/* Directly under the totals: an admin who has just read "total charged"
          and wants to check it against the processor is one click away, rather
          than scrolling past the cases and the ledger to find the exit. */}
      <StripeCustomerLink url={context.stripeCustomerUrl} />

      <div>
        <SectionTitle>Order cases</SectionTitle>
        {context.orders.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>No order cases yet.</Help>
        ) : (
          <DataTable
            label="Patient order cases"
            head={
              <>
                <Th>Journey</Th>
                <Th>State</Th>
                <Th style={{ textAlign: 'right' }}>Monthly</Th>
                <Th>Created</Th>
              </>
            }
          >
            {context.orders.map((order) => (
              <tr key={order.orderCaseId}>
                <Td>{order.lane === 'form' ? 'Form journey' : order.lane === 'callback' ? 'Callback journey' : order.lane}</Td>
                <Td>
                  <Badge tone={toneFor(order.state)}>{labelFor(order.state)}</Badge>
                </Td>
                <Td style={moneyCellStyle}>{order.monthlyPence === null ? '—' : formatMonthly(order.monthlyPence)}</Td>
                <Td>{formatUtcDate(order.createdAt)}</Td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>

      <div>
        <SectionTitle>Subscriptions</SectionTitle>
        {context.subscriptions.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>No subscriptions yet.</Help>
        ) : (
          <DataTable
            label="Patient subscriptions"
            head={
              <>
                <Th>Plan</Th>
                <Th style={{ textAlign: 'right' }}>Monthly</Th>
                <Th>State</Th>
                <Th>Next payment</Th>
                <Th>Payments</Th>
              </>
            }
          >
            {context.subscriptions.map((sub) => (
              <tr key={sub.subscriptionId}>
                <Td>
                  {sub.productKey} ({sub.months} mo)
                </Td>
                <Td style={moneyCellStyle}>{formatMonthly(sub.monthlyPence)}</Td>
                <Td>
                  <Badge tone={toneFor(sub.state)}>{labelFor(sub.state)}</Badge>
                </Td>
                <Td>{formatUtcDate(sub.nextPaymentDate) || '—'}</Td>
                <Td>
                  <Link
                    to={paymentsHref(sub.subscriptionId)}
                    style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gps-link)' }}
                  >
                    Open in Payments
                  </Link>
                </Td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>

      <div>
        <SectionTitle>Payment ledger</SectionTitle>
        {context.payments.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>No payments recorded yet.</Help>
        ) : (
          <DataTable
            label="Patient payment ledger"
            head={
              <>
                <Th>Payment</Th>
                <Th style={{ textAlign: 'right' }}>Amount</Th>
                <Th>Phase</Th>
                <Th>Date</Th>
              </>
            }
          >
            {context.payments.map((payment) => (
              <tr key={payment.paymentRecordId}>
                <Td>{PAYMENT_KIND_LABEL[payment.kind] ?? payment.kind}</Td>
                <Td style={moneyCellStyle}>{formatPence(payment.amountPence)}</Td>
                <Td>
                  <Badge tone={toneFor(payment.phase)}>{labelFor(payment.phase)}</Badge>
                </Td>
                <Td>{formatUtcDate(payment.occurredAt)}</Td>
              </tr>
            ))}
          </DataTable>
        )}
      </div>
    </div>
  );
}

/* ── Questionnaires — read-only assessment summaries ─────────────────────── */

function QuestionnairesPanel({
  assessmentIds,
  dealsStatus,
}: {
  assessmentIds: string[];
  dealsStatus: 'loading' | 'ready' | 'error';
}) {
  const [summaries, setSummaries] = useState<AssessmentSummary[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const key = assessmentIds.join(',');
  const load = useCallback(() => {
    setStatus('loading');
    Promise.all(assessmentIds.map((id) => getAssessmentSummary(id)))
      .then((data) => {
        setSummaries(data);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the ids' identity (a stable string), the array itself is rebuilt each render
  }, [key]);
  useEffect(() => {
    load();
  }, [load]);

  // The id set comes from the deals read — wait for it before declaring empty.
  if (dealsStatus === 'loading' || status === 'loading') {
    return <DrawerSkeleton blocks={2} label="Loading the patient's questionnaires…" />;
  }
  if (status === 'error') return <ErrorState message="Sorry — we could not load the questionnaires." onRetry={load} />;
  if (assessmentIds.length === 0 || !summaries || summaries.length === 0) {
    return <Help style={{ marginBottom: 0 }}>No questionnaires for this patient yet.</Help>;
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      {summaries.map((summary) => (
        <section
          key={summary.assessmentId}
          aria-label={`Questionnaire ${summary.assessmentId}`}
          style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-sm)', padding: 'var(--gps-s-3)' }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--gps-s-2)', marginBottom: 'var(--gps-s-2)' }}>
            <Badge tone={toneFor(summary.state)}>{labelFor(summary.state)}</Badge>
            {summary.eligibility ? <Badge tone={toneFor(summary.eligibility)}>{labelFor(summary.eligibility)}</Badge> : null}
            {summary.bmi != null ? (
              <span style={{ fontSize: '13.5px', color: 'var(--gps-muted)' }}>BMI {summary.bmi}</span>
            ) : null}
          </div>
          {/* Read-only summary — section titles + answer counts, never raw
              JSON and never an editing surface (the clinical review owns that). */}
          {summary.sections.length === 0 ? (
            <Help style={{ marginBottom: 0 }}>No answers were recorded for this assessment.</Help>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '4px' }}>
              {summary.sections.map((section) => (
                <li key={section.key} style={{ fontSize: '13.5px', color: 'var(--gps-text)' }}>
                  {section.title}
                  <span style={{ color: 'var(--gps-muted)' }}>
                    {' '}
                    — {section.itemCount} answer{section.itemCount === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}
